import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import './Welcome.css';

const API_URL = process.env.REACT_APP_API_URL;

function Welcome() {
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      if (user.role === 'user_pending') {
        navigate('/consent');
      } else if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/menu');
      }
    }
  }, [user, navigate]);

  // Extract access code from URL parameters (for QR code scanning)
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      setAccessCode(codeFromUrl);
    }
  }, []);

  useEffect(() => {
    console.log('Targeting API URL:', API_URL);
    const bubbles = document.querySelectorAll('.bubble');
    const animations = Array.from(bubbles).map((bubble, index) =>
      bubble.animate(
        [
          { transform: 'translateY(0px)' },
          { transform: 'translateY(-10px)' },
          { transform: 'translateY(0px)' },
        ],
        {
          duration: 6000 + index * 900,
          iterations: Infinity,
          easing: 'ease-in-out',
        }
      )
    );

    return () => {
      animations.forEach((animation) => animation.cancel());
    };
  }, []);

  const handleUserSubmit = async (event) => {
    event.preventDefault();
    if (!accessCode.trim()) {
      setError('Please enter your access code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-access`, {
        accessCode: accessCode.trim().toUpperCase(),
      });

      if (response.data.registrationRequired) {
        // Save the temporary token so the consent form can use it
        login(response.data.token, response.data.user);
        navigate('/consent');
      } else {
        login(response.data.token, response.data.user);
        navigate('/menu');
      }
    } catch (err) {
      // Show specific error message from server, or a friendly fallback
      const errorMessage = err.response?.data?.message;
      if (errorMessage) {
        setError(errorMessage);
      } else if (err.code === 'ERR_NETWORK') {
        setError('Cannot connect to server. Please check your internet connection.');
      } else {
        setError('Invalid access code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Admin login is now at /admin

  return (
    <div className="welcome-container">
      <div className="background" />
      
      {/* Sculpted wave surfaces */}
      <div className="light-rays">
        <div className="wave wave-top"></div>
        <div className="wave wave-middle"></div>
        <div className="wave wave-bottom"></div>
      </div>

      {/* Glow orbs with 3D effect */}
      <div className="bubble-sphere bubble-left">
        <div className="sphere-inner">
          <img src="/heart.png" alt="Heart" className="bubble-img" />
        </div>
      </div>
      <div className="bubble-sphere bubble-right-top">
        <div className="sphere-inner">
          <img src="/skull.png" alt="Skull" className="bubble-img" />
        </div>
      </div>
      <div className="bubble-sphere bubble-right-bottom">
        <div className="sphere-inner">
          <img src="/cell.png" alt="Cell" className="bubble-img" />
        </div>
      </div>
      <div className="bubble-sphere bubble-left-bottom">
        <div className="sphere-inner">
          <img src="/brain.png" alt="Brain" className="bubble-img" />
        </div>
      </div>

      <div className="content">
        <img src="/DarkLogo.png" alt="METATIP Logo" className="metatip-logo" />
        
        <p className="description">
          Explore three-dimensional educational AR experiences with METATIP.
        </p>

        {/* Access Code Card */}
        <div className="access-card">
          <form onSubmit={handleUserSubmit}>
            <h2>Access Code Login</h2>
            <input
              type="text"
              placeholder="Enter your code"
              value={accessCode}
              onChange={(event) => setAccessCode(event.target.value.toUpperCase())}
              autoComplete="one-time-code"
              disabled={loading}
              required
            />
            {error && <div className="error-message">{error}</div>}
            <button type="submit" disabled={loading || !accessCode.trim()}>
              {loading ? 'Verifying...' : 'Continue'}
            </button>
            <p className="note">Authorized users only</p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Welcome;
